import { useCallback, useEffect, useState } from "react";
import { Rainbow, Users, ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * PublicSidePill — the same pill as the authed SidePill but for logged-out
 * marketing pages (Landing, LiveNow). Persists the visitor's choice in
 * localStorage so it survives across marketing routes, and fires an
 * `onChange` callback so parent pages can re-fetch their side-filtered
 * previews (Trending Communities, Live Now grid, etc.).
 *
 * Default value = "lgbtq" per the app's historical audience. Visitors can
 * flip to "straight" any time; the choice persists but is deliberately
 * separate from any logged-in user's real `user.side` (that lives in the
 * database and is set by SidePicker on first login).
 */
const STORAGE_KEY = "pandax_public_side";
const SIDE_META = {
  lgbtq:    { label: "LGBTQ",    Icon: Rainbow, accent: "text-rose-400 border-rose-500/40 hover:border-rose-500" },
  straight: { label: "Straight", Icon: Users,   accent: "text-sky-300 border-sky-400/40 hover:border-sky-400" },
};

export function readPublicSide() {
  if (typeof window === "undefined") return "lgbtq";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "straight" ? "straight" : "lgbtq";
}

export default function PublicSidePill({ value, onChange, className = "" }) {
  const [internalSide, setInternalSide] = useState("lgbtq");
  const side = value ?? internalSide;

  useEffect(() => {
    if (value === undefined) setInternalSide(readPublicSide());
  }, [value]);

  const switchTo = useCallback((next) => {
    if (next === side) return;
    if (value === undefined) setInternalSide(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
    onChange && onChange(next);
  }, [side, value, onChange]);

  const current = SIDE_META[side];
  const { Icon } = current;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="public-side-pill"
          data-side={side}
          className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-xs font-medium bg-black/40 transition-colors ${current.accent} ${className}`}
        >
          <Icon size={12} />
          <span data-testid="public-side-pill-label">{current.label} side</span>
          <ChevronDown size={11} className="opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-black border border-white/10 rounded-none min-w-[220px]"
        data-testid="public-side-pill-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-3 py-2">
          Preview a side
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5" />
        {Object.entries(SIDE_META).map(([key, meta]) => {
          const Ico = meta.Icon;
          const active = side === key;
          return (
            <DropdownMenuItem
              key={key}
              onClick={() => switchTo(key)}
              data-testid={`public-side-pill-option-${key}`}
              className={`flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer rounded-none focus:bg-white/5 ${active ? "text-white" : "text-zinc-400 hover:text-white"}`}
            >
              <span className="flex items-center gap-2">
                <Ico size={14} className={meta.accent.split(" ")[0]} />
                {meta.label} side
              </span>
              {active && <Check size={13} className="text-rose-400" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator className="bg-white/5" />
        <div className="px-3 py-2 text-[10px] text-zinc-600 leading-relaxed">
          Sign up to lock in your side and unlock personalized feed + live.
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
